import React from 'react';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useTranslation } from 'react-i18next';

import { TabBasicData } from './tabs/TabBasicData';
import { TabAnnuities } from './tabs/TabAnnuities';
import { TabNote } from './tabs/TabNote';
import { TabDanExams } from './tabs/TabDanExams';
import { TabLicenses } from './tabs/TabLicenses';

/**
 * Dialog principal do afiliado - orquestra as 5 tabs.
 * 
 * Nao gerencia estado proprio - recebe tudo via props do AffiliateList.
 *
 * Props principais:
 *  - visible: bool
 *  - onHide: fecha o dialog
 *  - onSave: salva o afiliado
 *  - affiliate, onInputChange, submitted: estado do formulario
 *  - activeIndex, setActiveIndex: qual tab esta ativa
 *  - dropdowns e handlers pra dropdowns (dojo, state, grade, status, active)
 *  - callbacks pra abrir dialogs internos: onAddAnnuity, onEditAnnuity, etc
 */
export function AffiliateFormDialog({
    // Dialog control
    visible,
    onHide,
    onSave,

    // Form state
    affiliate,
    onInputChange,
    submitted,
    activeIndex,
    setActiveIndex,

    // Dropdowns
    dojos,
    states,
    grades,
    selectedGym,
    selectedState,
    selectedGrade,
    selectedPaid,
    selectedActive,
    onGymChange,
    onStateChange,
    onGradeChange,
    onPaidChange,
    onActiveChange,
    statusPayment,
    statusActive,

    // Erro de API (ex: telefone inválido devolvido pelo backend)
    apiError,

    // Annuities
    onAddAnnuity,
    onEditAnnuity,
    onDeleteAnnuity,

    // Dan Exams
    onAddDanExam,
    onEditDanExam,
    onDeleteDanExam,

    // Instructor Licenses
    onAddInstructorLicense,
    onEditInstructorLicense,
    onDeleteInstructorLicense,

    // Examiner Licenses
    onAddExaminerLicense,
    onEditExaminerLicense,
    onDeleteExaminerLicense,

    // Judge Licenses
    onAddJudgeLicense,
    onEditJudgeLicense,
    onDeleteJudgeLicense,
}) {
    const { t } = useTranslation();

    const footer = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={onHide} />
            <Button label="Save" icon="pi pi-check" onClick={onSave} />
        </>
    );

    return (
        <Dialog
            visible={visible}
            breakpoints={{ '1024px': '90vw', '640px': '120vw' }}
            style={{ width: '65vw' }}
            header="Dados do associado"
            modal
            className="p-fluid"
            footer={footer}
            onHide={onHide}
        >
            {apiError && (
                <Message
                    severity="error"
                    text={apiError}
                    className="w-full mb-3"
                    style={{ justifyContent: 'flex-start' }}
                />
            )}
            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                <TabPanel header="Dados básicos">
                    <TabBasicData
                        affiliate={affiliate}
                        onInputChange={onInputChange}
                        submitted={submitted}
                        dojos={dojos}
                        states={states}
                        grades={grades}
                        selectedGym={selectedGym}
                        selectedState={selectedState}
                        selectedGrade={selectedGrade}
                        selectedPaid={selectedPaid}
                        selectedActive={selectedActive}
                        onGymChange={onGymChange}
                        onStateChange={onStateChange}
                        onGradeChange={onGradeChange}
                        onPaidChange={onPaidChange}
                        onActiveChange={onActiveChange}
                        statusPayment={statusPayment}
                        statusActive={statusActive}
                    />
                </TabPanel>

                <TabPanel header={t('msg.jkaAffiliateAnnuities')}>
                    <TabAnnuities
                        affiliate={affiliate}
                        onAdd={onAddAnnuity}
                        onEdit={onEditAnnuity}
                        onDelete={onDeleteAnnuity}
                    />
                </TabPanel>

                <TabPanel header={t('msg.jkaNote')}>
                    <TabNote affiliate={affiliate} onInputChange={onInputChange} />
                </TabPanel>

                <TabPanel header={t('msg.jkaDanExams')}>
                    <TabDanExams
                        affiliate={affiliate}
                        onAdd={onAddDanExam}
                        onEdit={onEditDanExam}
                        onDelete={onDeleteDanExam}
                    />
                </TabPanel>

                <TabPanel header={t('msg.jkaInstructorLicense')}>
                    <TabLicenses
                        affiliate={affiliate}
                        type="instructor"
                        onAdd={onAddInstructorLicense}
                        onEdit={onEditInstructorLicense}
                        onDelete={onDeleteInstructorLicense}
                    />
                </TabPanel>

                <TabPanel header={t('msg.jkaExaminerLicense')}>
                    <TabLicenses
                        affiliate={affiliate}
                        type="examiner"
                        onAdd={onAddExaminerLicense}
                        onEdit={onEditExaminerLicense}
                        onDelete={onDeleteExaminerLicense}
                    />
                </TabPanel>

                <TabPanel header={t('msg.jkaJudgeLicense')}>
                    <TabLicenses
                        affiliate={affiliate}
                        type="judge"
                        onAdd={onAddJudgeLicense}
                        onEdit={onEditJudgeLicense}
                        onDelete={onDeleteJudgeLicense}
                    />
                </TabPanel>
            </TabView>
        </Dialog>
    );
}
